import { useCallback, useEffect, useRef, useState } from "react";
import FavoriteIcon from "@mui/icons-material/Favorite";
import AirIcon from "@mui/icons-material/Air";
import {
  getXandarClientLiveVitals,
  getXandarClientVitals,
} from "../../api/xandarVitalsService";
import { fetchClientHealthConfig } from "../../api/watchService";
import {
  classifyVitalSeverity,
  srOnlyStyle,
  vitalRangeColor,
  vitalSeverityA11yLabel,
  vitalSeverityMarker,
} from "../../utils/vitalSeverity";

/** Match backend XANDAR_LIVE_VITALS_MAX_AGE_MS — hide stale live samples. */
export const XANDAR_LIVE_MAX_AGE_MS = 5 * 60 * 1000;
/** Poll live endpoint only (does not re-hit the quota-limited day archive). */
const LIVE_POLL_MS = 5 * 1000;
/** Advance displayed age without a network round-trip. */
const LIVE_AGE_TICK_MS = 15 * 1000;

/**
 * Xandar Kardian vital-sign readings for the sensor bound to a client.
 *
 * Two things this deliberately does NOT do:
 *
 *  - It never shows a number the sensor did not report. A missing vital renders
 *    as an em dash, because a fabricated 0 BPM reads as an emergency.
 *  - It never collapses "quiet", "unbound" and "failed" into one empty list.
 *    Those are three different situations for a caregiver, so they are three
 *    different states here.
 *
 * Completed-day readings remain the baseline (the vendor rejects "today").
 * When the webhook path has stored a fresh sample (#542), a live strip sits
 * above that day view with its own "as of" age — never silently swapped.
 */

const LABEL_STYLE = {
  fontSize: "0.75rem",
  color: "#888",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

// Intl formatter construction is expensive and `formatReadingDay` runs on every
// render, so the day formatters are built once per timezone and reused (#531).
// An invalid zone throws in the constructor below, before the `set`, so only
// zones the platform itself accepted are ever cached — the map is bounded by
// the IANA database, not by traffic.
const DAY_PART_FORMATTERS = new Map();
function dayPartFormatter(timeZone) {
  let f = DAY_PART_FORMATTERS.get(timeZone);
  if (!f) {
    // The locale is pinned deliberately and is NOT a display choice: it fixes
    // the calendar and the digits used to build the key. Under a locale that
    // defaults to a non-Gregorian calendar (ar-SA) or non-Latin digits, the
    // parts come back as e.g. 1447 / ٢٠٢٦ and no longer compare against the
    // backend's ISO date. Both are named outright so a later "use the user's
    // locale" cleanup cannot silently reintroduce it.
    f = new Intl.DateTimeFormat("en-US", {
      calendar: "gregory",
      numberingSystem: "latn",
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    DAY_PART_FORMATTERS.set(timeZone, f);
  }
  return f;
}

// Display only, so it follows the reader's locale like every other date in this
// app (all 9 existing toLocale* call sites pass no locale). Unlike the key
// formatter above, layout here is cosmetic.
const PRETTY_DAY = new Intl.DateTimeFormat(undefined, {
  timeZone: "UTC",
  month: "short",
  day: "numeric",
});

/** `YYYY-MM-DD` in `timeZone`, built from PARTS — the locale's own string layout
 *  is not guaranteed by the spec, so it is never parsed. */
function localDayKey(ms, timeZone) {
  try {
    const parts = dayPartFormatter(timeZone).formatToParts(new Date(ms));
    const get = (t) => parts.find((x) => x.type === t)?.value;
    const y = get("year");
    const m = get("month");
    const d = get("day");
    if (!y || !m || !d) return null;
    return `${y}-${m}-${d}`;
  } catch {
    return null;
  }
}

/** One CALENDAR day before `YYYY-MM-DD`. Duration arithmetic (`- 86400000`) is
 *  wrong here: on a 23-hour DST day it lands two days back, so the day the
 *  caregiver is reading silently stops saying "Yesterday" (#531 review). */
function previousDayKey(dayKey) {
  const [y, m, d] = dayKey.split("-").map(Number);
  const t = new Date(Date.UTC(y, m - 1, d, 12));
  t.setUTCDate(t.getUTCDate() - 1);
  const p = (n) => String(n).padStart(2, "0");
  return `${t.getUTCFullYear()}-${p(t.getUTCMonth() + 1)}-${p(t.getUTCDate())}`;
}

/**
 * The vendor cannot return the current day, so every reading here is from a
 * completed one. A caregiver should not have to subtract dates to work out that
 * "2026-08-17" means yesterday, and the browser's day is the wrong reference —
 * a caregiver may sit in a different zone than the client.
 *
 * Without a client zone this returns the date untouched: a confidently wrong
 * "Yesterday" on a vital-signs card is worse than a plain date.
 */
export function formatReadingDay(readingDate, clientTimezone, nowMs = Date.now()) {
  if (!readingDate) return readingDate ?? null;
  if (!clientTimezone) return readingDate;

  const today = localDayKey(nowMs, clientTimezone);
  if (!today) return readingDate;

  const pretty = (() => {
    const parts = readingDate.split("-").map(Number);
    if (parts.length !== 3 || parts.some(Number.isNaN)) return readingDate;
    const [y, m, d] = parts;
    // Noon UTC keeps the label on the intended calendar day in every zone.
    return PRETTY_DAY.format(Date.UTC(y, m - 1, d, 12));
  })();

  if (readingDate === today) return `Today \u00b7 ${pretty}`;
  if (readingDate === previousDayKey(today)) return `Yesterday \u00b7 ${pretty}`;
  return pretty;
}

/** Human age for a live sample — minutes when under an hour, else hours. */
export function formatLiveAge(ageMs) {
  if (typeof ageMs !== "number" || !Number.isFinite(ageMs) || ageMs < 0) {
    return null;
  }
  const minutes = Math.floor(ageMs / 60000);
  if (minutes < 1) return "just now";
  if (minutes === 1) return "1 minute ago";
  if (minutes < 60) return `${minutes} minutes ago`;
  const hours = Math.floor(minutes / 60);
  if (hours === 1) return "1 hour ago";
  return `${hours} hours ago`;
}

/**
 * Caregiver-facing location from room + primary-zone flags.
 * XK calls the primary ROI `region_presence` (often configured as a "bed"
 * region in their portal). Many CompassCare rooms have no bed — desk, chair,
 * recliner — so we never say "bed" to caregivers. Never surface vendor field
 * names (presence / region_presence).
 */
export function formatXandarPresenceStatus(presence, regionPresence) {
  if (typeof presence !== "number" || !Number.isFinite(presence)) return null;
  if (presence === -1) return "Sensor starting";
  if (presence === 0) return "Room empty";
  if (presence !== 1) return null;

  if (regionPresence === 1) return "In monitored zone";
  if (regionPresence === 0) return "In room, outside zone";
  if (regionPresence === -1) return "In room (zone sensor starting)";
  return "In room";
}

/** How long the current location state has lasted (vendor dwelltime, seconds). */
export function formatXandarDwell(dwellSeconds) {
  if (typeof dwellSeconds !== "number" || !Number.isFinite(dwellSeconds)) {
    return null;
  }
  if (dwellSeconds < 0) return null;
  const secs = Math.floor(dwellSeconds);
  // GMA-1933: firmware streams dwelltime=0 for the whole vacant period — not
  // a one-second transition. Labelling it "for 1 second" forever is a false cue.
  if (secs === 0) return null;
  if (secs < 60) return secs <= 1 ? "for 1 second" : `for ${secs} seconds`;
  const minutes = Math.floor(secs / 60);
  if (minutes < 60) {
    return minutes === 1 ? "for 1 minute" : `for ${minutes} minutes`;
  }
  const hours = Math.floor(minutes / 60);
  return hours === 1 ? "for 1 hour" : `for ${hours} hours`;
}

/** Latest non-null value of `key`, by that reading's own timestamp. */
function latestReading(points, key, timestampKey) {
  const withValue = points.filter(
    (p) => p && typeof p[key] === "number" && Number.isFinite(p[key]),
  );
  if (!withValue.length) return null;
  return withValue.reduce((best, p) => {
    const at = p[timestampKey] || p.bucket_timestamp || "";
    const bestAt = best[timestampKey] || best.bucket_timestamp || "";
    return at > bestAt ? p : best;
  });
}

function VitalTile({ icon, label, value, unit, testId, severityLevel = "unclassified" }) {
  const valueColor = vitalRangeColor(severityLevel, { surface: "light" }) || "#333";
  const marker = vitalSeverityMarker(severityLevel);
  const a11ySeverity = vitalSeverityA11yLabel(severityLevel);
  // Explicit normal branch: unclassified/absent readings get a neutral border,
  // never the clinical green (matches the vitalRangeColor null contract).
  const borderColor =
    severityLevel === "critical"
      ? "#F5C6C6"
      : severityLevel === "caution"
        ? "#FFE082"
        : severityLevel === "normal"
          ? "#C8E6C9"
          : "#E0E0E0";
  return (
    <div
      data-vital-severity={severityLevel}
      style={{
        flex: "1 1 140px",
        padding: "12px 14px",
        backgroundColor: "#fff",
        border: `1px solid ${borderColor}`,
        borderRadius: "6px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
        {icon}
        <span style={LABEL_STYLE}>{label}</span>
      </div>
      <div style={{ marginTop: "4px" }}>
        <span
          data-testid={testId}
          style={{ fontSize: "1.6rem", fontWeight: 600, color: valueColor }}
        >
          {value === null ? "—" : value}
        </span>
        {value !== null && (
          <span style={{ fontSize: "0.85rem", color: "#777", marginLeft: "4px" }}>
            {unit}
          </span>
        )}
        {/* Severity is never color-only: visible marker + announced word. */}
        {marker ? (
          <span
            aria-hidden="true"
            style={{
              marginLeft: "6px",
              fontSize: "1rem",
              fontWeight: 700,
              color: valueColor,
            }}
          >
            {marker}
          </span>
        ) : null}
        {value !== null && a11ySeverity ? (
          <span style={srOnlyStyle}>{a11ySeverity}</span>
        ) : null}
      </div>
    </div>
  );
}

/**
 * `nowMs` is a test seam, not part of the rendering contract: it exists so the
 * DST cases can pin "today" without faking the global clock. No production call
 * site passes it (XandarKardianSection passes clientId/clientTimezone/
 * canManageIntegration only), and it defaults to the real clock — overriding it
 * in app code would freeze which day reads as "Yesterday".
 */
export default function XandarVitalsPanel({
  clientId,
  date,
  clientTimezone = null,
  canManageIntegration = false,
  nowMs,
  thresholdsVersion = 0,
}) {
  const [state, setState] = useState({ status: "loading", data: null, live: null });
  const [customThresholds, setCustomThresholds] = useState({});
  // Guard against a slow load for a previous client/date committing after the
  // panel moved on (same protection as WatchVitalsSection's statusGen): only
  // the newest load may write state or thresholds.
  const loadGen = useRef(0);
  // Separate generation for the client-scoped threshold fetch — it no longer
  // rides on `load`, so it needs its own stale-response guard.
  const thresholdGen = useRef(0);

  // Thresholds are decoration ON the readings, so the readings must never wait
  // for them: the config endpoint is admin-only, making it the request most
  // likely to hang or be rejected for a caregiver, and it used to sit inside
  // the day/live Promise.allSettled — a slow config held settled vitals in the
  // loading state. It is also per-CLIENT, so keeping it out of `load` stops a
  // date change refetching it. A client switch resets to defaults first, so
  // the new client is never colored by the previous client's bands.
  useEffect(() => {
    setCustomThresholds({});
    // `load()` deliberately carries prev.live through a reload so a DATE change
    // does not blank the live strip — but that must never span a CLIENT change:
    // if the new client has no live sample yet, the previous client's heart and
    // breathing rate would render under this client's name, colored by this
    // client's bands. Readings are per-client; clear them here so the strip
    // starts empty and only the new client's own sample can fill it.
    setState({ status: "loading", data: null, live: null });
  }, [clientId]);

  useEffect(() => {
    if (!clientId) return undefined;
    const gen = ++thresholdGen.current;
    let cancelled = false;
    // Promise.resolve keeps the tolerance the previous Promise.allSettled had:
    // a stub returning a non-promise must not throw synchronously here.
    Promise.resolve(fetchClientHealthConfig(clientId))
      .then((config) => {
        if (cancelled || gen !== thresholdGen.current) return;
        const custom = config?.customThresholds;
        setCustomThresholds(custom && typeof custom === "object" ? custom : {});
      })
      .catch((err) => {
        // Preserve whatever is already in force; committing {} here would
        // repaint a critical reading green on a transient failure.
        console.warn("Xandar vitals threshold config load failed", err);
      });
    return () => {
      cancelled = true;
    };
    // thresholdsVersion bumps when an admin saves in the sibling settings card.
  }, [clientId, thresholdsVersion]);

  const load = useCallback(async () => {
    if (!clientId) return;
    const gen = ++loadGen.current;
    // Keep the last live sample while the day archive reloads — live is
    // client-scoped, not day-scoped, so a date change must not blank it.
    // #586: keep the last loaded DAY too. The card labels itself with the day
    // it holds (xk-reading-date), so showing it through a reload — and through
    // a failed reload below — is honest, while blanking it takes the only
    // vitals off the screen. A CLIENT switch still clears both via the
    // clientId effect above; that boundary is what must never leak.
    setState((prev) => ({ status: "loading", data: prev.data, live: prev.live }));
    // Null live responses keep prev.live; the age ticker drops stale samples.
    let nextLive = null;
    try {
      // Day archive and live sample are independent: a live miss must not
      // blank the completed-day card, and a day failure must not hide live.
      const [dayResult, liveResult] = await Promise.allSettled([
        getXandarClientVitals(clientId, date),
        getXandarClientLiveVitals(clientId),
      ]);
      if (gen !== loadGen.current) return;

      const livePayload =
        liveResult.status === "fulfilled" ? liveResult.value : null;
      nextLive =
        livePayload &&
        livePayload.live &&
        typeof livePayload.live === "object"
          ? livePayload.live
          : null;

      if (dayResult.status !== "fulfilled") {
        console.warn(
          "Xandar vitals day load failed",
          dayResult.reason || new Error("Xandar vitals day load failed"),
        );
        setState((prev) => ({
          status: "error",
          data: prev.data,
          live: nextLive ?? prev.live,
        }));
        return;
      }
      const data = dayResult.value;
      if (
        !data ||
        typeof data.deviceBound !== "boolean" ||
        !Array.isArray(data.points)
      ) {
        throw new Error("Unexpected Xandar vitals payload");
      }

      setState((prev) => ({
        status: "ready",
        data,
        live: nextLive ?? prev.live,
      }));
    } catch (err) {
      if (gen !== loadGen.current) return;
      console.warn("Xandar vitals load failed", err);
      setState((prev) => ({
        status: "error",
        data: prev.data,
        live: nextLive ?? prev.live,
      }));
    }
  }, [clientId, date]);

  useEffect(() => {
    load();
  }, [load]);

  // Refresh live independently so a stopped stream eventually falls back to
  // the completed-day card (does not re-fetch the quota-consuming day archive).
  // Stop while the tab is hidden — same lifecycle as IotEventHistorySection.
  // Self-schedule after each poll settles so a slow request cannot overlap and
  // overwrite a newer sample with an older response.
  const hasLiveSample = Boolean(state.live);
  // Poll while day archive is ready/failed, or while we already hold a live
  // sample (e.g. date change reload). Do not depend on live identity — that
  // would restart the timer on every successful poll.
  useEffect(() => {
    if (!clientId) return undefined;
    if (
      state.status !== "ready" &&
      state.status !== "error" &&
      !hasLiveSample
    ) {
      return undefined;
    }
    let cancelled = false;
    let timer = null;
    let inFlight = false;

    const scheduleNext = () => {
      if (cancelled || document.visibilityState === "hidden") return;
      if (timer !== null) clearTimeout(timer);
      timer = setTimeout(poll, LIVE_POLL_MS);
    };

    const poll = async () => {
      if (cancelled || inFlight) return;
      inFlight = true;
      try {
        const livePayload = await getXandarClientLiveVitals(clientId);
        if (cancelled) return;
        const next =
          livePayload &&
          livePayload.live &&
          typeof livePayload.live === "object"
            ? livePayload.live
            : null;
        setState((prev) =>
          prev.status === "ready" ||
          prev.status === "error" ||
          prev.live
            ? { ...prev, live: next ?? prev.live }
            : prev,
        );
      } catch {
        // Keep the last good live sample; age tick will drop it when stale.
      } finally {
        inFlight = false;
        scheduleNext();
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        if (timer !== null) {
          clearTimeout(timer);
          timer = null;
        }
      } else {
        poll();
      }
    };

    if (document.visibilityState !== "hidden") poll();
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      cancelled = true;
      if (timer !== null) clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [clientId, state.status, hasLiveSample]);

  // Advance ageMs locally; drop the strip once past the live window.
  useEffect(() => {
    if (!state.live?.eventAt) return undefined;
    const tick = () => {
      setState((prev) => {
        if (!prev.live?.eventAt) return prev;
        const ageMs = Date.now() - new Date(prev.live.eventAt).getTime();
        if (!Number.isFinite(ageMs) || ageMs > XANDAR_LIVE_MAX_AGE_MS) {
          return { ...prev, live: null };
        }
        if (prev.live.ageMs === ageMs) return prev;
        return { ...prev, live: { ...prev.live, ageMs } };
      });
    };
    const id = setInterval(tick, LIVE_AGE_TICK_MS);
    return () => clearInterval(id);
  }, [state.live?.eventAt]);

  if (!clientId) return null;

  if (state.status === "loading" && !state.live && !state.data) {
    return (
      <div
        role="status"
        style={{ fontSize: "0.9rem", color: "#777", padding: "10px 0" }}
      >
        Loading vital signs…
      </div>
    );
  }

  // #586: the full-card error is only for having NOTHING to show — with a held
  // day card or a live strip, the failure renders as a banner above them.
  if (state.status === "error" && !state.live && !state.data) {
    return (
      <div
        data-testid="xk-vitals-error"
        style={{
          padding: "12px 14px",
          backgroundColor: "#FFF4F4",
          border: "1px solid #F5C6C6",
          borderRadius: "6px",
          fontSize: "0.9rem",
          color: "#8A3A3A",
        }}
      >
        Could not load vital signs right now.{" "}
        <button
          type="button"
          onClick={load}
          className="btn-link"
          style={{
            background: "none",
            border: "none",
            padding: 0,
            color: "#8A3A3A",
            textDecoration: "underline",
            cursor: "pointer",
            font: "inherit",
          }}
        >
          Try again
        </button>
      </div>
    );
  }

  const { deviceBound, serialNumber, date: readingDate, points, remainingQuota } =
    state.data || {};
  const live = state.live;
  const dayFailed = state.status === "error";

  if (!deviceBound && !live) {
    return (
      <div
        data-testid="xk-no-device"
        style={{
          padding: "12px 14px",
          backgroundColor: "#fff",
          border: "1px dashed #C8E6C9",
          borderRadius: "6px",
          fontSize: "0.9rem",
          color: "#666",
        }}
      >
        No Xandar sensor is linked to this client yet, so there are no readings
        to show. Link the sensor&rsquo;s serial number to this client to start
        collecting vital signs.
      </div>
    );
  }

  const hr = latestReading(points || [], "hr", "hr_timestamp");
  const rr = latestReading(points || [], "rr", "rr_timestamp");
  // GMA-1933: presence=0 still streams noise-floor HR/BR indefinitely. Never
  // paint those as vitals (or severity colors) — belt-and-suspenders vs BE #1214.
  const roomEmpty = live && live.presence === 0;
  const liveHr =
    !roomEmpty && live && typeof live.heartRate === "number"
      ? live.heartRate
      : null;
  const liveRr =
    !roomEmpty && live && typeof live.breathingRate === "number"
      ? live.breathingRate
      : null;
  const liveHrRounded =
    liveHr !== null ? Math.round(liveHr * 10) / 10 : null;
  const liveRrRounded =
    liveRr !== null ? Math.round(liveRr * 10) / 10 : null;
  const dayHr = hr ? Math.round(hr.hr * 10) / 10 : null;
  const dayRr = rr ? Math.round(rr.rr * 10) / 10 : null;
  // Classify the RAW readings — rounding is a display concern, and rounding
  // first disagrees with the backend engine at a band edge (raw 109.96 is
  // below warn_above 110 but rounds to 110.0, so the web would call a reading
  // abnormal that the engine, and any alert it raises, calls normal).
  const liveHrLevel = classifyVitalSeverity(
    liveHr,
    "heart_rate",
    customThresholds,
  );
  const liveRrLevel = classifyVitalSeverity(
    liveRr,
    "respiratory_rate",
    customThresholds,
  );
  const dayHrLevel = classifyVitalSeverity(
    hr ? hr.hr : null,
    "heart_rate",
    customThresholds,
  );
  const dayRrLevel = classifyVitalSeverity(
    rr ? rr.rr : null,
    "respiratory_rate",
    customThresholds,
  );
  const presenceStatus = live
    ? formatXandarPresenceStatus(live.presence, live.regionPresence)
    : null;
  const dwellLabel = live ? formatXandarDwell(live.dwelltime) : null;
  const hasLive =
    liveHr !== null || liveRr !== null || presenceStatus !== null;
  const liveAgeLabel = hasLive ? formatLiveAge(live.ageMs) : null;

  return (
    <div style={{ marginBottom: "12px" }}>
      {dayFailed && (
        <div
          data-testid="xk-vitals-error"
          style={{
            marginBottom: "10px",
            padding: "12px 14px",
            backgroundColor: "#FFF4F4",
            border: "1px solid #F5C6C6",
            borderRadius: "6px",
            fontSize: "0.9rem",
            color: "#8A3A3A",
          }}
        >
          {/* #586: name what IS on screen. With a held day card the banner
              points at it; only the live-strip-only case may promise live
              vitals, or the banner promises something that is not there. */}
          {state.data
            ? "Could not load that day’s readings. Showing the last loaded day below."
            : "Could not load that day’s readings. Live vitals below are still updating."}{" "}
          <button
            type="button"
            onClick={load}
            className="btn-link"
            style={{
              background: "none",
              border: "none",
              padding: 0,
              color: "#8A3A3A",
              textDecoration: "underline",
              cursor: "pointer",
              font: "inherit",
            }}
          >
            Try again
          </button>
        </div>
      )}

      {serialNumber ? (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "baseline",
            gap: "8px",
            marginBottom: "8px",
          }}
        >
          <span style={LABEL_STYLE}>Sensor</span>
          <span
            data-testid="xk-serial"
            style={{ fontFamily: "monospace", fontSize: "0.9rem" }}
          >
            {serialNumber}
          </span>
        </div>
      ) : null}

      {hasLive && (
        <div
          data-testid="xk-live-strip"
          style={{
            marginBottom: "10px",
            padding: "10px 12px",
            backgroundColor: "#F1F8E9",
            border: "1px solid #C5E1A5",
            borderRadius: "6px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "baseline",
              gap: "8px",
              marginBottom: "8px",
            }}
          >
            <span style={LABEL_STYLE}>Live now</span>
            <span data-testid="xk-live-age" style={{ fontSize: "0.9rem" }}>
              {liveAgeLabel ? `as of ${liveAgeLabel}` : "recent sample"}
            </span>
          </div>
          {presenceStatus && (
            <div style={{ marginBottom: "10px" }}>
              <div
                data-testid="xk-live-presence"
                style={{
                  fontSize: "1.15rem",
                  fontWeight: 600,
                  color: "#33691E",
                  lineHeight: 1.35,
                }}
              >
                {presenceStatus}
                {dwellLabel ? (
                  <span
                    data-testid="xk-live-dwell"
                    style={{
                      fontWeight: 400,
                      fontSize: "0.95rem",
                      color: "#558B2F",
                      marginLeft: "6px",
                    }}
                  >
                    {dwellLabel}
                  </span>
                ) : null}
              </div>
              {presenceStatus === "Room empty" ? (
                <p
                  data-testid="xk-live-presence-hint"
                  style={{
                    margin: "6px 0 0 0",
                    fontSize: "0.8rem",
                    fontWeight: 400,
                    color: "#555",
                    lineHeight: 1.4,
                  }}
                >
                  No one is detected in the room. Heart and breathing numbers
                  stay hidden while the room is empty — noise-floor samples are
                  not vitals.
                </p>
              ) : null}
            </div>
          )}
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <VitalTile
              icon={
                <FavoriteIcon
                  style={{
                    color: vitalRangeColor(liveHrLevel, { surface: "light" }),
                    fontSize: 18,
                  }}
                />
              }
              label="Heart rate"
              value={liveHrRounded}
              unit="BPM"
              testId="xk-live-hr"
              severityLevel={liveHrLevel}
            />
            <VitalTile
              icon={
                <AirIcon
                  style={{
                    color: vitalRangeColor(liveRrLevel, { surface: "light" }),
                    fontSize: 18,
                  }}
                />
              }
              label="Breathing rate"
              value={liveRrRounded}
              unit="per minute"
              testId="xk-live-rr"
              severityLevel={liveRrLevel}
            />
          </div>
        </div>
      )}

      {/* #586: a failed day load hides the day section only when there is no
          held day to show — a card that already loaded stays on screen, dated
          by xk-reading-date to the day it actually holds. */}
      {dayFailed && !state.data ? null : (
        <>
          {readingDate && (deviceBound || (points && points.length)) ? (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "baseline",
                gap: "8px",
                marginBottom: "8px",
                marginTop: hasLive ? "4px" : 0,
              }}
            >
              <span data-testid="xk-completed-day-heading" style={LABEL_STYLE}>
                Completed day
              </span>
              <span data-testid="xk-reading-date" style={{ fontSize: "0.9rem" }}>
                {formatReadingDay(readingDate, clientTimezone, nowMs)}
              </span>
            </div>
          ) : null}
          {points && points.length ? (
            <>
              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                <VitalTile
                  icon={
                    <FavoriteIcon
                      style={{
                        color: vitalRangeColor(dayHrLevel, { surface: "light" }),
                        fontSize: 18,
                      }}
                    />
                  }
                  label="Heart rate"
                  value={dayHr}
                  unit="BPM"
                  testId="xk-hr-latest"
                  severityLevel={dayHrLevel}
                />
                <VitalTile
                  icon={
                    <AirIcon
                      style={{
                        color: vitalRangeColor(dayRrLevel, { surface: "light" }),
                        fontSize: 18,
                      }}
                    />
                  }
                  label="Respiratory rate"
                  value={dayRr}
                  unit="RPM"
                  testId="xk-rr-latest"
                  severityLevel={dayRrLevel}
                />
              </div>
              <p
                style={{
                  fontSize: "0.8rem",
                  color: "#777",
                  margin: "8px 0 0 0",
                  lineHeight: 1.4,
                }}
              >
                Latest of{" "}
                <span data-testid="xk-reading-count">{points.length}</span>{" "}
                readings from that completed day (the vendor does not return
                today&rsquo;s archive yet). Heart rate and respiratory rate are
                measured separately, so their most recent readings may be from
                different times.
                {canManageIntegration &&
                  typeof remainingQuota === "number" && (
                    <> Vendor lookups remaining today: {remainingQuota}.</>
                  )}
              </p>
            </>
          ) : (
            <div
              data-testid="xk-vitals-empty"
              style={{
                padding: "12px 14px",
                backgroundColor: "#fff",
                border: "1px solid #EEE",
                borderRadius: "6px",
                fontSize: "0.9rem",
                color: "#666",
              }}
            >
              {hasLive
                ? `No completed-day archive for ${readingDate || "that day"} yet. Live values above are from the sensor right now.`
                : `The sensor is linked but recorded no readings for ${readingDate || "that day"}. A sensor can be online and still report nothing if no one was in range.`}
            </div>
          )}
        </>
      )}
    </div>
  );
}
